import { useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AlertCircle, ShieldAlert, VideoOff } from "lucide-react";
import { useAdminRecordingWatch } from "@/hooks/useAdminRecordingWatch";
import { usePageMeta } from "@/hooks/usePageMeta";
import { WatchPlayer } from "@/components/watch/WatchPlayer";
import { StatusCard, LoadingRow } from "@/components/watch/StatusCards";
import { formatDateShort } from "@/utils/dateFormat";
import { formatUserName } from "@/utils/userName";

/**
 * Admin-only /admin/watch/:recordingId page: a standalone dark viewer that
 * plays exactly one recording (all of its chapters, in order) via the same
 * WatchPlayer the public /watch page uses. Deliberately minimal compared to
 * WatchPage: no AngleSwitcher (a single angle), no ShareMenu, no live stream,
 * and NO view counting — admin spot-checks must not inflate creator-facing
 * view counts. Backend access control is JWT + the ADMIN_EMAILS allowlist.
 */
export default function AdminRecordingWatchPage() {
  const { recordingId = "" } = useParams();
  const { t } = useTranslation("pages");
  const { recording, session, isLoading, error, refetch } =
    useAdminRecordingWatch(recordingId);
  usePageMeta({
    title: session?.title ?? recording?.angleName ?? undefined,
  });

  const handlePlaybackAuthError = useCallback(() => {
    // A fatal playlist error is usually token expiry — refetching mints a
    // fresh playbackUrl (harmless no-op if the failure was transient).
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex h-dvh min-h-screen w-full flex-col bg-black">
        <LoadingRow label={t("watch.loading", { ns: "pages" })} />
      </div>
    );
  }

  const errorStatus = (
    error as { response?: { status?: number } } | null
  )?.response?.status;

  let content: ReactNode;
  if (errorStatus === 403) {
    content = (
      <StatusCard
        icon={<ShieldAlert className="h-6 w-6" />}
        title={t("watch.adminForbidden.title", { ns: "pages" })}
        body={t("watch.adminForbidden.body", { ns: "pages" })}
      />
    );
  } else if (error && !recording) {
    // Data wins over background errors: TanStack Query v5 keeps `data` when a
    // later refetch fails (e.g. the pending/partial poll or the playback-auth
    // recovery path), so a playable recording must survive a transient
    // failure. Only show the error card when we have nothing to play. The 403
    // branch above is unaffected — a 403 never coexists with data.
    content = (
      <StatusCard
        icon={<AlertCircle className="h-6 w-6" />}
        title={t("watch.adminUnavailable.title", { ns: "pages" })}
        body={t("watch.adminUnavailable.body", { ns: "pages" })}
        hint={t("watch.unavailable.hint", { ns: "pages" })}
      />
    );
  } else if (
    recording &&
    (recording.playbackStatus === "ready" ||
      recording.playbackStatus === "partial")
  ) {
    content = (
      <div className="min-h-screen w-full bg-black">
        <div className="mx-auto w-full max-w-[960px] pb-16 pt-6 sm:px-5">
          <WatchPlayer
            fragment={recording}
            onPlaybackAuthError={handlePlaybackAuthError}
            className="max-sm:rounded-none"
          />
          <div className="px-4 pt-4 sm:px-0">
            {session ? (
              <>
                <h1 className="text-lg font-semibold text-white">
                  {session.title}
                </h1>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-white/60">
                  {recording.recordedAt && (
                    <>
                      <span>{formatDateShort(recording.recordedAt)}</span>
                      <span aria-hidden="true">·</span>
                    </>
                  )}
                  <span>{session.event.name}</span>
                  <span aria-hidden="true">·</span>
                  <span>{session.track.name}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {formatUserName(session.driver)}
                  </span>
                </div>
              </>
            ) : (
              <h1 className="text-lg font-semibold text-white">
                {recording.angleName ?? t("watch.noVideo", { ns: "pages" })}
              </h1>
            )}
          </div>
        </div>
      </div>
    );
  } else if (recording && recording.playbackStatus === "pending") {
    content = <LoadingRow label={t("watch.processing", { ns: "pages" })} />;
  } else {
    // playbackStatus === "unavailable" (or a defensive fallback): the
    // recording exists but no chapter ever produced playable HLS.
    content = (
      <StatusCard
        icon={<VideoOff className="h-6 w-6" />}
        title={t("watch.adminUnavailable.title", { ns: "pages" })}
        body={t("watch.adminUnavailable.body", { ns: "pages" })}
      />
    );
  }

  return (
    <div className="flex h-dvh min-h-screen w-full flex-col bg-black">
      {content}
    </div>
  );
}
